/**
 * Voxel lighting: flood-filled skylight + blocklight with incremental
 * add/remove updates (the classic two-queue algorithm).
 *
 * Channels: skylight (0..15, comes from the sky, dims with the day/night
 * cycle in the shader) and blocklight (0..15, from emitters like glowstone
 * and lava). Both are stored packed in one byte per voxel.
 *
 * Light spreads with -1 per step (sunlight drops straight down at full
 * strength) and extra attenuation through filtering blocks like water.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { IS_OPAQUE, LIGHT_EMISSION, LIGHT_FILTER } from '../blocks';
import { Chunk, CHUNK_AREA, ChunkState, OPEN_SKY, World, worldKey } from './chunk';

const SKY = 0;
const BLOCK = 1;

/** Queue entry encoding: x,y,z packed + level. Flat number array for speed. */
type LightQueue = number[];

/**
 * Chunk-local access cache. Light BFS touches a bounded region of chunks;
 * caching the Map lookups turns every voxel visit into typed-array math.
 */
class LightAccess {
  private readonly cache = new Map<number, Chunk | null>();
  readonly dirty = new Set<number>();

  constructor(private readonly world: World) {}

  // One-entry memo in front of the Map: BFS neighbours almost always share a chunk.
  private lastCx = 0x7fffffff;
  private lastCz = 0x7fffffff;
  private lastChunk: Chunk | null = null;
  private lastDirty: Chunk | null = null;

  /** Chunk holding world column (wx, wz), or null if not loaded / not generated. */
  chunkOf(wx: number, wz: number): Chunk | null {
    const cx = wx >> 4;
    const cz = wz >> 4;
    if (cx === this.lastCx && cz === this.lastCz) return this.lastChunk;
    this.lastCx = cx;
    this.lastCz = cz;
    return (this.lastChunk = this.lookup(cx, cz));
  }

  private lookup(cx: number, cz: number): Chunk | null {
    const key = (cx & 31) * 64 + (cz & 31);
    let c = this.cache.get(key);
    if (c === undefined) {
      c = this.world.getChunk(cx, cz) ?? null;
      if (c && c.state < ChunkState.Generated) c = null; // no voxel data yet
      this.cache.set(key, c);
    }
    return c;
  }

  isLoaded(wx: number, wz: number): boolean {
    return this.chunkOf(wx, wz) !== null;
  }

  getBlock(wx: number, wy: number, wz: number): number {
    if (wy < 0) return 13;
    if (wy >= WORLD_HEIGHT) return 0;
    const c = this.chunkOf(wx, wz);
    if (!c) return 0;
    const i = (wx & 15) + (wz & 15) * CHUNK_SIZE + wy * CHUNK_AREA;
    return i < c.blocks.length ? c.blocks[i] : 0;
  }

  getLight(wx: number, wy: number, wz: number, channel: number): number {
    if (wy >= WORLD_HEIGHT) return channel === SKY ? 15 : 0;
    if (wy < 0) return 0;
    const c = this.chunkOf(wx, wz);
    if (!c) return channel === SKY ? 15 : 0;
    const i = (wx & 15) + (wz & 15) * CHUNK_SIZE + wy * CHUNK_AREA;
    const l = i < c.light.length ? c.light[i] : OPEN_SKY; // above a short chunk's arrays (chunk.ts)
    return channel === SKY ? l >> 4 : l & 0xf;
  }

  setLight(wx: number, wy: number, wz: number, channel: number, level: number): void {
    if (wy < 0 || wy >= WORLD_HEIGHT) return;
    const c = this.chunkOf(wx, wz);
    if (!c) return;
    const i = (wx & 15) + (wz & 15) * CHUNK_SIZE + wy * CHUNK_AREA;
    if (i >= c.light.length) return;
    const old = c.light[i];
    const next = channel === SKY ? (level << 4) | (old & 0xf) : (old & 0xf0) | level;
    if (next !== old) {
      c.light[i] = next;
      this.markDirty(c);
    }
  }

  markDirty(c: Chunk): void {
    if (c === this.lastDirty) return;
    this.lastDirty = c;
    c.meshDirty = true;
    this.dirty.add(worldKey(c.cx, c.cz));
  }
}

/**
 * Propagate light outwards from every queued position. Queue layout:
 * [x0,y0,z0,level0, x1,y1,z1,level1, ...] consumed with a head pointer.
 */
const DX = [1, -1, 0, 0, 0, 0];
const DY = [0, 0, 1, -1, 0, 0];
const DZ = [0, 0, 0, 0, 1, -1];

function propagate(acc: LightAccess, queue: LightQueue, channel: number): void {
  let head = 0;
  while (head < queue.length) {
    const wx = queue[head++];
    const wy = queue[head++];
    const wz = queue[head++];
    const level = queue[head++];
    if (level <= 1) continue;

    for (let d = 0; d < 6; d++) {
      const nx = wx + DX[d];
      const ny = wy + DY[d];
      const nz = wz + DZ[d];
      if (ny < 0 || ny >= WORLD_HEIGHT) continue;
      const c = acc.chunkOf(nx, nz);
      if (c === null) continue; // unloaded chunks light themselves later
      const li = (nx & 15) + (nz & 15) * CHUNK_SIZE + ny * CHUNK_AREA;
      if (li >= c.blocks.length) continue; // above a short chunk's arrays: open sky already, and block light has faded out by there
      const nb = c.blocks[li];
      if (IS_OPAQUE[nb] === 1) continue;
      // Sunlight travels straight down without losing strength.
      const straightDownSun = channel === SKY && DY[d] === -1 && level === 15;
      let newLevel = straightDownSun ? 15 : level - 1;
      newLevel -= LIGHT_FILTER[nb];
      if (newLevel <= 0) continue;
      const old = c.light[li];
      if (channel === SKY) {
        if (old >> 4 >= newLevel) continue;
        c.light[li] = (newLevel << 4) | (old & 0xf);
      } else {
        if ((old & 0xf) >= newLevel) continue;
        c.light[li] = (old & 0xf0) | newLevel;
      }
      acc.markDirty(c);
      queue.push(nx, ny, nz, newLevel);
    }
  }
}

/**
 * Remove light starting from (wx,wy,wz) which used to have `oldLevel`.
 * Neighbors with independent (higher or equal) light are re-queued as sources.
 */
function removeLight(
  acc: LightAccess,
  removalQueue: LightQueue,
  propQueue: LightQueue,
  channel: number
): void {
  let head = 0;
  while (head < removalQueue.length) {
    const wx = removalQueue[head++];
    const wy = removalQueue[head++];
    const wz = removalQueue[head++];
    const level = removalQueue[head++];

    for (let d = 0; d < 6; d++) {
      const nx = wx + DX[d];
      const ny = wy + DY[d];
      const nz = wz + DZ[d];
      if (ny < 0 || ny >= WORLD_HEIGHT) continue;
      const c = acc.chunkOf(nx, nz);
      if (c === null) continue;
      const li = (nx & 15) + (nz & 15) * CHUNK_SIZE + ny * CHUNK_AREA;
      if (li >= c.blocks.length) {
        // above a short chunk's arrays: open sky, which survives and re-fills the removed area
        if (channel === SKY) propQueue.push(nx, ny, nz, 15);
        continue;
      }
      if (IS_OPAQUE[c.blocks[li]] === 1) continue;
      const old = c.light[li];
      const nl = channel === SKY ? old >> 4 : old & 0xf;
      if (nl === 0) continue;

      const sunColumn = channel === SKY && DY[d] === -1 && level === 15 && nl === 15;
      if (nl < level || sunColumn) {
        c.light[li] = channel === SKY ? old & 0xf : old & 0xf0;
        acc.markDirty(c);
        removalQueue.push(nx, ny, nz, nl);
      } else {
        // Brighter neighbor: it survives and will re-fill the removed area.
        propQueue.push(nx, ny, nz, nl);
      }
    }
  }
}

// --- chunk-local lighting (runs in the gen worker) ---------------------------

/** Growable scratch queue of cell indices, reused across calls. */
let localQueue = new Int32Array(1 << 16);
let localTail = 0;

function pushLocal(i: number): void {
  if (localTail === localQueue.length) {
    const next = new Int32Array(localQueue.length * 2);
    next.set(localQueue);
    localQueue = next;
  }
  localQueue[localTail++] = i;
}

/** Flood the queued cells' light through the chunk (never leaves it). */
function propagateLocal(blocks: Uint16Array, light: Uint8Array, channel: number): void {
  const top = blocks.length / CHUNK_AREA - 1;
  let head = 0;
  while (head < localTail) {
    const i = localQueue[head++];
    const level = channel === SKY ? light[i] >> 4 : light[i] & 0xf;
    if (level <= 1) continue;
    const x = i & 15;
    const z = (i >> 4) & 15;
    const y = i >> 8;
    for (let d = 0; d < 6; d++) {
      let ni: number;
      if (d === 0) { if (x === 15) continue; ni = i + 1; }
      else if (d === 1) { if (x === 0) continue; ni = i - 1; }
      else if (d === 2) { if (y === top) continue; ni = i + CHUNK_AREA; }
      else if (d === 3) { if (y === 0) continue; ni = i - CHUNK_AREA; }
      else if (d === 4) { if (z === 15) continue; ni = i + CHUNK_SIZE; }
      else { if (z === 0) continue; ni = i - CHUNK_SIZE; }
      const nb = blocks[ni];
      if (IS_OPAQUE[nb] === 1) continue;
      // Sunlight travels straight down without losing strength.
      let newLevel = channel === SKY && d === 3 && level === 15 ? 15 : level - 1;
      newLevel -= LIGHT_FILTER[nb];
      if (newLevel <= 0) continue;
      const old = light[ni];
      if (channel === SKY) {
        if (old >> 4 >= newLevel) continue;
        light[ni] = (newLevel << 4) | (old & 0xf);
      } else {
        if ((old & 0xf) >= newLevel) continue;
        light[ni] = (old & 0xf0) | newLevel;
      }
      pushLocal(ni);
    }
  }
  localTail = 0;
}

/**
 * Light a freshly generated chunk in isolation: sun columns, emitters and the
 * flood fill confined to the chunk's own cells. Pure typed-array work with no
 * world access, so it runs in the gen worker; `stitchChunk` later exchanges
 * light across the four borders on the main thread.
 *
 * The arrays may be short (chunk.ts): lighting starts from their top layer,
 * which is right because everything above it is air.
 */
export function lightChunkLocal(blocks: Uint16Array, light: Uint8Array): void {
  const volume = blocks.length;
  const height = volume / CHUNK_AREA;
  // --- Skylight column fill (with water/leaf attenuation) ---
  for (let c = 0; c < CHUNK_AREA; c++) {
    let level = 15;
    for (let i = c + volume - CHUNK_AREA; i >= 0 && level > 0; i -= CHUNK_AREA) {
      const id = blocks[i];
      if (IS_OPAQUE[id] === 1) break;
      level = Math.max(0, level - LIGHT_FILTER[id]);
      light[i] = (level << 4) | (light[i] & 0xf);
    }
  }

  // Seed: skylit cells that border something darker (cave mouths, cliffs, water).
  localTail = 0;
  for (let z = 0; z < CHUNK_SIZE; z++) {
    for (let x = 0; x < CHUNK_SIZE; x++) {
      for (let y = height - 1; y >= 0; y--) {
        const i = x + z * CHUNK_SIZE + y * CHUNK_AREA;
        const sky = light[i] >> 4;
        if (sky === 0) break; // below this everything is 0 until we leave shadow
        if (sky <= 1) continue;
        if (
          (y > 0 && IS_OPAQUE[blocks[i - CHUNK_AREA]] === 0 && (light[i - CHUNK_AREA] >> 4) < sky - 1) ||
          (x < 15 && IS_OPAQUE[blocks[i + 1]] === 0 && (light[i + 1] >> 4) < sky - 1) ||
          (x > 0 && IS_OPAQUE[blocks[i - 1]] === 0 && (light[i - 1] >> 4) < sky - 1) ||
          (z < 15 && IS_OPAQUE[blocks[i + CHUNK_SIZE]] === 0 && (light[i + CHUNK_SIZE] >> 4) < sky - 1) ||
          (z > 0 && IS_OPAQUE[blocks[i - CHUNK_SIZE]] === 0 && (light[i - CHUNK_SIZE] >> 4) < sky - 1)
        ) {
          pushLocal(i);
        }
      }
    }
  }
  propagateLocal(blocks, light, SKY);

  // Seed: emitters inside this chunk.
  for (let i = 0; i < volume; i++) {
    const e = LIGHT_EMISSION[blocks[i]];
    if (e > 0) {
      light[i] = (light[i] & 0xf0) | e;
      pushLocal(i);
    }
  }
  propagateLocal(blocks, light, BLOCK);
}

/**
 * Exchange light between a locally-lit chunk and its four horizontal
 * neighbours. Only border cell pairs whose levels differ by more than one
 * step are queued, so open sky and solid rock cost nothing. Marks every chunk
 * whose light changed as mesh-dirty via `dirty`.
 */
export function stitchChunk(world: World, chunk: Chunk, dirty: Set<number>): void {
  const acc = new LightAccess(world);
  const skyQueue: LightQueue = [];
  const blockQueue: LightQueue = [];
  const ox = chunk.cx * CHUNK_SIZE;
  const oz = chunk.cz * CHUNK_SIZE;

  for (let side = 0; side < 4; side++) {
    const nChunk = side === 0 ? world.getChunk(chunk.cx + 1, chunk.cz)
      : side === 1 ? world.getChunk(chunk.cx - 1, chunk.cz)
      : side === 2 ? world.getChunk(chunk.cx, chunk.cz + 1)
      : world.getChunk(chunk.cx, chunk.cz - 1);
    if (!nChunk || nChunk.state < ChunkState.Generated) continue;
    const nox = nChunk.cx * CHUNK_SIZE;
    const noz = nChunk.cz * CHUNK_SIZE;
    for (let a = 0; a < CHUNK_SIZE; a++) {
      // own border cell and the neighbour's cell just across the border
      const lx = side === 0 ? 15 : side === 1 ? 0 : a;
      const lz = side === 2 ? 15 : side === 3 ? 0 : a;
      const bx = side === 0 ? 0 : side === 1 ? 15 : a;
      const bz = side === 2 ? 0 : side === 3 ? 15 : a;
      let i = lx + lz * CHUNK_SIZE;
      let j = bx + bz * CHUNK_SIZE;
      // either chunk may be short (chunk.ts): above its arrays a chunk is open sky
      const al = chunk.light, bl = nChunk.light;
      const top = Math.max(al.length, bl.length) / CHUNK_AREA;
      for (let y = 0; y < top; y++, i += CHUNK_AREA, j += CHUNK_AREA) {
        const la = i < al.length ? al[i] : OPEN_SKY;
        const lb = j < bl.length ? bl[j] : OPEN_SKY;
        if (la === lb) continue;
        const sa = la >> 4, sb = lb >> 4;
        if (sa > sb + 1) skyQueue.push(ox + lx, y, oz + lz, sa);
        else if (sb > sa + 1) skyQueue.push(nox + bx, y, noz + bz, sb);
        const ba = la & 0xf, bb = lb & 0xf;
        if (ba > bb + 1) blockQueue.push(ox + lx, y, oz + lz, ba);
        else if (bb > ba + 1) blockQueue.push(nox + bx, y, noz + bz, bb);
      }
    }
  }

  propagate(acc, skyQueue, SKY);
  propagate(acc, blockQueue, BLOCK);

  chunk.lightDirty = false;
  for (const key of acc.dirty) dirty.add(key);
  dirty.add(worldKey(chunk.cx, chunk.cz));
}

/**
 * Full initial lighting for a chunk on the calling thread: local pass plus
 * border stitch. The live pipeline splits these (worker / main thread).
 */
export function lightChunk(world: World, chunk: Chunk, dirty: Set<number>): void {
  lightChunkLocal(chunk.blocks, chunk.light);
  stitchChunk(world, chunk, dirty);
}

/**
 * Incremental relight after a block edit. `oldId` is the previous block.
 *
 * Order of operations matters:
 *   1. remove the old emitter's contribution (blocklight removal BFS)
 *   2. if the new block attenuates more, remove light fed through the cell
 *   3. if the new block is more transparent, let neighbors re-fill the cell
 *   4. apply any new emission and propagate
 *
 * Marks every chunk whose light changed as mesh-dirty.
 */
export function relightAfterEdit(
  world: World,
  wx: number,
  wy: number,
  wz: number,
  oldId: number,
  newId: number,
  dirty: Set<number>
): void {
  const acc = new LightAccess(world);
  const newOpaque = IS_OPAQUE[newId] === 1;
  const oldFilter = LIGHT_FILTER[oldId];
  const newFilter = LIGHT_FILTER[newId];
  const oldEmission = LIGHT_EMISSION[oldId];
  const newEmission = LIGHT_EMISSION[newId];

  const propSky: LightQueue = [];
  const propBlock: LightQueue = [];
  const remSky: LightQueue = [];
  const remBlock: LightQueue = [];

  const oldSky = acc.getLight(wx, wy, wz, SKY);
  const oldBlock = acc.getLight(wx, wy, wz, BLOCK);

  // 1. Removing an emitter: strip its contribution from the blocklight field.
  let blockRemoval = false;
  if (oldEmission > newEmission) {
    remBlock.push(wx, wy, wz, Math.max(oldBlock, oldEmission));
    acc.setLight(wx, wy, wz, BLOCK, 0);
    blockRemoval = true;
  }

  // 2. New block attenuates more: remove sky + block light that flowed through.
  if (newOpaque || newFilter > oldFilter) {
    remSky.push(wx, wy, wz, oldSky);
    acc.setLight(wx, wy, wz, SKY, 0);
    if (!blockRemoval) {
      remBlock.push(wx, wy, wz, oldBlock);
      acc.setLight(wx, wy, wz, BLOCK, 0);
    }
  }

  removeLight(acc, remSky, propSky, SKY);
  removeLight(acc, remBlock, propBlock, BLOCK);

  // 3. New block is more transparent: neighbors re-fill the opened cell.
  if (!newOpaque && newFilter <= oldFilter) {
    const DX = [1, -1, 0, 0, 0, 0];
    const DY = [0, 0, 1, -1, 0, 0];
    const DZ = [0, 0, 0, 0, 1, -1];
    for (let d = 0; d < 6; d++) {
      const nx = wx + DX[d];
      const ny = wy + DY[d];
      const nz = wz + DZ[d];
      if (ny < 0 || ny >= WORLD_HEIGHT) continue;
      if (!acc.isLoaded(nx, nz)) continue;
      const s = acc.getLight(nx, ny, nz, SKY);
      const b = acc.getLight(nx, ny, nz, BLOCK);
      if (s > 1) propSky.push(nx, ny, nz, s);
      if (b > 1) propBlock.push(nx, ny, nz, b);
    }
  }

  // 4. New emitter.
  if (newEmission > 0) {
    const cur = acc.getLight(wx, wy, wz, BLOCK);
    if (newEmission > cur) {
      acc.setLight(wx, wy, wz, BLOCK, newEmission);
      propBlock.push(wx, wy, wz, newEmission);
    }
  }

  propagate(acc, propSky, SKY);
  propagate(acc, propBlock, BLOCK);

  for (const key of acc.dirty) dirty.add(key);
}
